import React, { FC, useMemo, useState, useContext } from 'react'
import { CommonPageProps } from '../../../types/interface/fsm.interface'
import PageWrapper from '@dg-shared/PageWrapper'
import { ExternalSubmitBtn, LDContainerV2FSM, LMBasicV3FSM } from '@dg-shared'
import { useTranslate } from '../../../hooks/shared'
import { FORM_ID, ORIGINAL_POLICY, TRANSLATIONS as T } from '../../../constants'
import { formatPolicyDate, getDataComponentName } from '@dg-util'
import { Card, Divider } from '@mui/material'
import Loading from '../../qb/Loading/Page'
import { makeStyles } from 'tss-react/mui'
import { Theme } from '@mui/material/styles'
import PolicyReviewCardHeader from '@dg-shared/PolicyReviewCardHeader'
import useLogic from './useLogic'
import FormBuilder from '@dg-shared/forms/FormBuilder'
import PolicyReviewVehicleDetails from '@dg-shared/PolicyReviewVehicleDetails'
import PolicyVerifyCard from '@dg-shared/PolicyVerifyCard'
import { FormState } from 'final-form'
import { VerifyFormValues } from '../../../types/interface/form.interface'
import { Events } from '../../../FSM/shared/constants'
import useReactQueryParty from '../../../hooks/shared/useReactQueryParty'
import { Context } from '@dg-shared/ContextProvider'
import {
  definePolicyChangeStartDate,
  definePolicyNotChangeDate,
} from '../../../util/defineOfferDate'
import { ProductName } from '../../../types/interface/quote.interface'
import { PolicyReviewCardProps } from '../../../types'
import PolicyReviewTravelDetails from '@dg-shared/PolicyReviewTravelDetails'
import PolicyReviewContentDetails from '@dg-shared/PolicyReviewContentDetails'
import { PolicyV2Type } from 'src/types/ApiV2'

const useStyles = makeStyles()((theme: Theme) => ({
  backgroundImage: {
    [theme.breakpoints.up('md')]: {
      ...theme.brand.assets.pageBackgrounds.verify,
    },
  },

  columns: {
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(3),
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
    gap: theme.spacing(3),
    '&>div': {
      flex: '2 1 auto',
    },
  },

  actionButtons: {
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: theme.spacing(3),
      paddingBottom: theme.spacing(1),
      '& > button:not(:last-child)': {
        marginBottom: theme.spacing(3),
      },
    },
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
      justifyItems: 'center',
      justifyContent: 'center',
      '& > button:not(:last-child)': {
        marginRight: theme.spacing(2),
      },
    },
  },

  headerDate: {
    display: 'none',
  },

  cardWrapper: {
    marginTop: 14,
  },

  headerContainer: {
    margin: theme.spacing(3),
    padding: 0,
    paddingBottom: theme.spacing(1),
    marginBottom: 0,
  },

  externalButton: {
    marginTop: theme.spacing(3),
  },

  divider: {
    opacity: 0.4,
    margin: `0 ${theme.spacing(3)} 0  ${theme.spacing(3)}`,
  },
}))

const PagePolicyVerify: FC<CommonPageProps> = ({ service }) => {
  const { t } = useTranslate(T.POLICY_VERIFY)
  const { classes } = useStyles()
  const { backBtn, isLoading, policy, productProps, changePolicy } = useLogic()
  const { updateParty, party, isLoading: isPartyLoading } = useReactQueryParty()
  const [btnDisabled, setBtnDisabled] = useState<boolean>(false)
  const { context, setContext } = useContext(Context)
  const original = context[ORIGINAL_POLICY] as Partial<typeof policy>

  const definedDate = useMemo(() => {
    if (new Date(policy.InsuredFrom) < new Date()) {
      return definePolicyNotChangeDate(1)
    } else {
      return definePolicyChangeStartDate(policy)
    }
  }, [policy])

  const isMileageChanged = useMemo(() => {
    if (!original || policy.productName !== ProductName.VEHICLE) return false
    return Number(policy.AnnualMileage) !== Number(original.AnnualMileage)
  }, [policy, original])

  const hasNonMileagePolicyChanges = useMemo(() => {
    if (!original) return false
    return (
      ['CarComprehensiveDeductible', 'PreviousClaims', 'YoungDrivers'] as (keyof Pick<
        PolicyV2Type,
        'CarComprehensiveDeductible' | 'PreviousClaims' | 'YoungDrivers'
      >)[]
    ).some((field) => String(policy[field]) !== String(original[field]))
  }, [policy, original])

  const isPolicyDateDisabled = isMileageChanged && !hasNonMileagePolicyChanges

  // DFE-4030: when the date field is disabled (mileage-only change), pre-fill
  // the picker with the original policy start so the user sees a date instead
  // of an empty input. The BFF overrides changeDate with OriginalInsuredFrom
  // server-side, so this value is purely informational.
  const disabledDateValue = useMemo(
    () => (original?.InsuredFrom ? new Date(original.InsuredFrom) : undefined),
    [original]
  )

  const [formValue, setFormValue] = useState<VerifyFormValues>({})

  if (isLoading || isPartyLoading) {
    return <Loading />
  }

  const externalSubmitBtn = (
    <ExternalSubmitBtn
      customClassName={classes.externalButton}
      text={t('submit')}
      targetId={FORM_ID.POLICY_VERIFY}
      disabled={btnDisabled}
    />
  )

  const vehiclePolicyChange = async (values: VerifyFormValues) => {
    let actualPolicy = { ...policy }

    if (isMileageChanged) {
      // DFE-4030: BFF overrides changeDate with OriginalInsuredFrom when this
      // flag is set, so previously-amended policies still backdate to the true
      // policy start rather than to the current version's InsuredFrom.
      const actualInsuredFrom = formatPolicyDate(new Date(original.InsuredFrom))
      const backdatedChange = await changePolicy({
        changeDate: actualInsuredFrom,
        backdateToPolicyStart: true,
        ...original,
        AnnualMileage: policy.AnnualMileage,
      })

      if (!hasNonMileagePolicyChanges) {
        // Mileage-only path: the date field is disabled and `values.policyDate`
        // is undefined. Returning here avoids formatting an undefined date.
        return
      }

      // DFE-4103: carry the endorsement's own response forward, NOT refetchPolicy().
      // That query is pinned to ['policy', policyNumber, policyVersion] and the call
      // above just created a NEW version, so the refetch returns the pre-change policy
      // and the second call below would write the old driving distance straight back.
      actualPolicy = backdatedChange.data
    }

    const changeDate = formatPolicyDate(values.policyDate)

    const extractedPolicyChanges = {
      CarComprehensiveDeductible: policy.CarComprehensiveDeductible,
      PreviousClaims: policy.PreviousClaims,
      YoungDrivers: policy.YoungDrivers,
    }

    await changePolicy({
      changeDate,
      ...actualPolicy,
      ...extractedPolicyChanges,
    })
  }

  const submitFormHandler = async (values: VerifyFormValues) => {
    await updateParty({
      Email: values.email,
      MobilePhone: values.phone,
    })

    if (policy.productName === ProductName.VEHICLE) {
      await vehiclePolicyChange(values)
    } else {
      const changeDate = formatPolicyDate(values.policyDate)
      await changePolicy({ changeDate, ...policy })
    }

    setContext({ [ORIGINAL_POLICY]: null })
    service.send({ type: Events.NEXT })
  }

  const policyReviewCardDetailsPerProduct: Record<ProductName, React.FC<PolicyReviewCardProps>> = {
    [ProductName.VEHICLE]: PolicyReviewVehicleDetails,
    [ProductName.TRAVEL]: PolicyReviewTravelDetails,
    [ProductName.CONTENT]: PolicyReviewContentDetails,
  }

  const PolicyDetails = policyReviewCardDetailsPerProduct[policy.productName]

  const content = policy && party && (
    <div data-componentname={getDataComponentName(__filename)} className={classes.cardWrapper}>
      <Card data-testid='verify-policy-card' elevation={3} className={classes.backgroundImage}>
        <PolicyReviewCardHeader
          policy={policy}
          classDate={classes.headerDate}
          classContainer={classes.headerContainer}
        />
        <Divider className={classes.divider} />
        <div className={classes.columns}>
          <div>
            <FormBuilder
              id={FORM_ID.POLICY_VERIFY}
              filledFieldsData={formValue}
              handleBtnDisabled={setBtnDisabled}
              initialValues={{
                policyDate: isPolicyDateDisabled ? disabledDateValue : definedDate.selected,
                email: party.Email,
                phone: party.MobilePhone?.slice(3), // remove first 3 numbers since it is a code (ex. +47)
              }}
              setFormState={({ values }: FormState<never>): void => setFormValue(values)}
              validationArgs={{
                ...definedDate,
                // DFE-4030: when disabled the value is the original policy
                // start (in the past), so we must widen `min` to accept it —
                // otherwise Joi `date.min` blocks submit.
                min:
                  isPolicyDateDisabled && disabledDateValue ? disabledDateValue : definedDate.min,
                disabled: isPolicyDateDisabled,
              }}
              submitHandler={submitFormHandler}
              isFormControllable
              customSubmit
            />
            <PolicyDetails policy={policy} productProps={productProps} />
          </div>
          <div>
            <PolicyVerifyCard policy={policy} />
          </div>
        </div>
      </Card>
      <section className={classes.actionButtons}>{externalSubmitBtn}</section>
    </div>
  )

  return (
    <PageWrapper
      title={t('title')}
      titleHeader={t('title')}
      mobile={LMBasicV3FSM}
      desktop={LDContainerV2FSM}
      breadcrumbsBtnText={t(backBtn.text)}
    >
      {content}
    </PageWrapper>
  )
}

export default PagePolicyVerify
